import set from "@rill/set";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { withTranslation } from "react-i18next";
import map from "lodash/map";
import React from "react";
import submit from "submit-form";
import { AU_STATES, US_STATES } from "../../../constants";
import { checkOrganizationSectionPermission } from "../../../middleware/ensure-coach-permissions";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import CancelAccountModal from "../../../parts/modals/cancel-account";
import UpdatePaymentModal from "../../../parts/modals/update-payment";

import UpgradeConfirmationModal from "../../../parts/modals/upgrade-confirmation";
import StripeCardComponent from "../../../parts/StripeCardComponent";
import SvgIcon from "../../../parts/SvgIcon";
import SettingsNav from "../../../parts/settings-nav";
import { RillContext } from "../../../rill/RillReact/share/base";
import loadStripeCard, { getSourceValues } from "../../../utils/stripe-card";
import { SettingsBackHandler } from "../../coaching-portal/settings/components/SettingsBackHandler";

const FORM_ID = "billing-card-address-details";

class BillingDetails extends React.Component {
  constructor(props) {
    super(props);
    const { activeUserToRequest } = props;
    this.state = {
      editCreditInfo: false,
      countrySelected:
        activeUserToRequest?.coachingBilling?.stripe?.address_country ??
        activeUserToRequest?.country,
      stateSelected:
        activeUserToRequest?.coachingBilling?.stripe?.address_state ?? "",
    };

    this.handleCreditToggle = this.handleCreditToggle.bind(this);
  }

  componentDidMount() {
    loadStripeCard(FORM_ID, this.stripeTokenHandler);
  }

  stripeTokenHandler(res) {
    submit("/actions/coach-billing/update/source", {
      method: "POST",
      body: {
        source: getSourceValues(),
        stripeToken: res,
      },
    });
  }

  handleCreditToggle() {
    this.setState(
      (prevState) => ({ editCreditInfo: !prevState.editCreditInfo }),
      () => {
        loadStripeCard(FORM_ID, this.stripeTokenHandler);
      }
    );
  }

  handleCountrySelect(e) {
    this.setState({ countrySelected: e.target.value });
  }
  handleStateSelect(e) {
    this.setState({ stateSelected: e.target.value });
  }

  render() {
    const { locals, req } = this.context;
    const {
      activeUser,
      getCountryName,
      countries: countryOptions,
      activeUserToRequest,
    } = locals;
    const { countrySelected, stateSelected, editCreditInfo } = this.state;
    const stripe = get(activeUserToRequest, "coachingBilling.stripe", {});
    const cardType = activeUserToRequest?.coachingBilling?.stripe?.brand
      ? activeUserToRequest.coachingBilling.stripe.brand
          .replace(" ", "-")
          .toLowerCase()
      : null;
    const { address_line1, address_line2, address_city, address_zip } =
      stripe || {};
    const addressLine2Render = address_line2 === null ? "" : address_line2;

    const { t } = this.props;
    return (
      <main className="settings manage-billing">
        <SettingsNav classNames="mobile-hidden" />
        <form
          method="POST"
          action="/actions/coach-billing/update/source"
          id={FORM_ID}
          className="manage-billing"
        >
          <h3 className="force-mobile-hidden">{t("settingsBilling.billingInformation")}</h3>
          <SettingsBackHandler title={t("settingsBilling.manageBilling")} />
          <p className="p2 -mt-4 mb-8">
            {t("settingsBilling.manageCardDescription")}
          </p>
          {isEmpty(stripe) ? (
            <div role="input-group">
              <label htmlFor="card-element">
                {t("settingsBilling.creditCardInformation")}
              </label>
              <StripeCardComponent />
            </div>
          ) : !editCreditInfo || req.query.closeEditCredit ? (
            <div className="payment-details">
              <label>{t("settingsBilling.yourPaymentDetails")}</label>
              <div className="card-details">
                <SvgIcon
                  className="cc-brand"
                  src={`/img/${cardType}-icon.svg`}
                />
                <strong>**** **** **** {stripe.last4}</strong>
                <a
                  role="button"
                  data-button-type="text"
                  href=""
                  onClick={this.handleCreditToggle}
                >
                  <span>{t("settingsBilling.changeCard")}</span>
                </a>
              </div>
            </div>
          ) : null}

          <div className="stripe-info">
            {editCreditInfo && !req.query.closeEditCredit && (
              <div role="input-group">
                <label htmlFor="card-element">
                  {t("settingsBilling.creditCardInformation")}
                </label>
                <StripeCardComponent />
              </div>
            )}
          </div>

          <section>
            <div className="input-group">
              <label htmlFor="source[address_line1]">
                {t("settingsBilling.billingAddress")}
              </label>
              <input
                className="text-input"
                name="source[address_line1]"
                type="text"
                placeholder={t("settingsBilling.addressLine1")}
                defaultValue={address_line1}
                sz="large"
              />
            </div>

            <div className="input-group">
              <label htmlFor="source[address_line2]">&nbsp;</label>
              <input
                className="text-input"
                name="source[address_line2]"
                type="text"
                placeholder={t("settingsBilling.addressLine2")}
                defaultValue={addressLine2Render}
                sz="large"
              />
            </div>

            <div role="input-group">
              <label htmlFor="source[address_city]">
                {t("settingsBilling.city")}
              </label>
              <input
                className="text-input"
                name="source[address_city]"
                type="text"
                placeholder={t("settingsBilling.cityPlaceholder")}
                defaultValue={address_city}
                sz="large"
              />
            </div>

            <div role="input-group">
              <label htmlFor="source[address_country]">
                {t("settingsBilling.country")}
              </label>
              <select
                required
                name="source[address_country]"
                className="select-input"
                value={countrySelected || get(activeUser, "country", "")}
                onChange={(e) => this.handleCountrySelect(e)}
                sz="large"
              >
                <option hidden disabled value="">
                  {t("settingsBilling.location")}
                </option>
                {map(countryOptions, (code) => {
                  return (
                    <option key={code} value={code}>
                      {getCountryName(code)}
                    </option>
                  );
                })}
              </select>
            </div>

            {["AU", "CA", "US"].indexOf(countrySelected) === -1 && (
              <div role="input-group">
                <label htmlFor="source[address_state]">
                  {t("settingsBilling.state")}
                </label>
                <input
                  className="text-input"
                  name="source[address_state]"
                  type="text"
                  placeholder={t("settingsBilling.statePlaceholder")}
                  value={stateSelected}
                  onChange={(e) => this.handleStateSelect(e)}
                  sz="large"
                />
              </div>
            )}

            {countrySelected === "CA" && (
              <div role="input-group">
                <label htmlFor="source[address_state]">
                  {t("settingsBilling.province")}
                </label>
                <select
                  className="select-input"
                  name="source[address_state]"
                  placeholder={t("settingsBilling.provincePlaceholder")}
                  value={stateSelected}
                  onChange={(e) => this.handleStateSelect(e)}
                  sz="large"
                >
                  <option disabled value="">
                    {t("settingsBilling.provincePlaceholder")}
                  </option>
                  <option value="AB">Alberta</option>
                  <option value="BC">British Columbia</option>
                  <option value="MB">Manitoba</option>
                  <option value="NB">New Brunswick</option>
                  <option value="NL">Newfoundland and Labrador</option>
                  <option value="NS">Nova Scotia</option>
                  <option value="ON">Ontario</option>
                  <option value="PE">Prince Edward Island</option>
                  <option value="QC">Quebec</option>
                  <option value="SK">Saskatchewan</option>
                  <option value="NT">Northwest Territories</option>
                  <option value="NU">Nunavut</option>
                  <option value="YT">Yukon</option>
                </select>
              </div>
            )}

            {countrySelected === "US" && (
              <div role="input-group">
                <label htmlFor="source[address_state]">
                  {t("settingsBilling.state")}
                </label>
                <select
                  className="select-input"
                  type="text"
                  name="source[address_state]"
                  value={stateSelected}
                  onChange={(e) => this.handleStateSelect(e)}
                  sz="large"
                >
                  <option disabled value="">
                    {t("settingsBilling.statePlaceholder")}
                  </option>
                  {US_STATES.map((state) => (
                    <option value={state.abbreviation} key={state.abbreviation}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {countrySelected === "AU" && (
              <div role="input-group">
                <label htmlFor="source[address_state]">
                  {t("settingsBilling.state")}
                </label>
                <select
                  className="select-input"
                  type="text"
                  name="source[address_state]"
                  value={stateSelected}
                  onChange={(e) => this.handleStateSelect(e)}
                  sz="large"
                >
                  <option disabled value="">
                    {t("settingsBilling.statePlaceholder")}
                  </option>
                  {AU_STATES.map((state) => (
                    <option value={state.abbreviation} key={state.abbreviation}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div role="input-group">
              <label htmlFor="source[address_zip]">
                {countrySelected === "US"
                  ? t("settingsBilling.zipCode")
                  : t("settingsBilling.postalCode")}
              </label>
              <input
                className="text-input"
                name="source[address_zip]"
                type="text"
                placeholder={
                  countrySelected === "US"
                    ? t("settingsBilling.zipCode")
                    : t("settingsBilling.postalCode")
                }
                defaultValue={address_zip}
                sz="large"
              />
            </div>
          </section>

          <button type="submit" data-button-type="primary">
            {t("common:save")}
          </button>
        </form>
      </main>
    );
  }
}

BillingDetails.contextType = RillContext;

const BillingDetailsWithI18n = withTranslation(["dashboard", "common"])(
  BillingDetails
);

const BillingDetailsController = (app) => {
  app.get(
    "/coach-manage-billing",
    set("heading", "Manage Billing"),
    (ctx, next) =>
      checkOrganizationSectionPermission(
        ctx,
        next,
        "settings",
        "/coaching-portal/settings",
        ["Owner"]
      ),
    ({ res, req, locals, session }) => {
      const { query } = req;

      const modal = get(query, "modal", {});
      locals.modal = {
        cancel: <CancelAccountModal />,
        "update-payment": <UpdatePaymentModal />,
        "upgrade-confirmation": <UpgradeConfirmationModal />,
        "activity-feed": <ActivityDrawer />,
      }[modal.type];

      res.body = (
        <BillingDetailsWithI18n
          activeUser={locals.activeUser}
          show
          stripe={query.action === "update stripe"}
          activeUserToRequest={locals.activeUserToRequest}
        />
      );
    }
  );
};

export default BillingDetailsController;
